import {
  isUnpublishedScope,
  readPublishedScopePath,
  SCOPE_PATH_PASSTHROUGH,
} from '../published-scope/unpublishedScope';
import { getByPath } from './getByPath';
import { parse } from './parse';
import type { Expression } from './types';

export interface InterpolateOptions {
  /** URL-encode each substituted token value in mixed templates. */
  readonly encodeSubstitutions?: boolean;
  /**
   * How to render a **path** expression that resolves to `undefined`
   * (a missing path):
   *
   * - `'empty'` (default) — coerce to `''`, preserving the historical
   *   behavior every existing consumer relies on (notably
   *   `fetchDataSource`, which must serialize missing paths as empty in
   *   URLs/bodies, never as a literal).
   * - `'literal'` — restore the original `{{<raw>}}` token. Used by callers
   *   (e.g. the admin label adapter) that want an unresolved token to render
   *   as-is instead of disappearing.
   *
   * Only `undefined` (an absent path) triggers literal restore. A resolved
   * `null` still coerces to `''`, and `functionCall` expressions still defer
   * to `undefined` (they are never resurrected as literals).
   */
  readonly onMissing?: 'empty' | 'literal';
}

/**
 * Evaluate a template string against a runtime context.
 *
 * **Return-type behavior** (ADR-12):
 *
 * - **Single-expression template** (`'{{results.totalCount}}'`): returns the
 *   raw typed value from the context — array, object, number, boolean,
 *   `null`, or `undefined` — so DB-1.3's DataSourceManager can forward
 *   typed results to body / query-param serialization.
 * - **Mixed or multiple expressions** (`'Hello {{name}}'` or
 *   `'{{a}}{{b}}'`): returns a **string**. Each expression is coerced:
 *   `undefined`/`null` → `''`, arrays → comma-joined (ADR-12 query-param
 *   rule), everything else → `String(value)`.
 *
 * Prefer single-expression templates for machine-consumed query/body
 * values when the path resolves to an array (raw array is preserved).
 * Mixed templates coerce arrays with `","` and are a poor fit for
 * human-readable labels — emit display strings at the context layer
 * (e.g. `_label` companions) when readable multi-select text is needed.
 *
 * **Comparison deferral:** `comparison.isSelected(arg)` (and any other
 * function-call form) always resolves to `undefined` in DB-1.2. DB-2.2
 * (ComparisonProvider) owns runtime evaluation. In mixed templates the
 * deferred result coerces to an empty string.
 *
 * Missing paths resolve silently to `undefined` per ADR-12. Syntax errors in
 * the template surface as `InterpolationError` from `parse()`.
 *
 * @example
 * interpolate('{{results.totalCount}}', { results: { totalCount: 42 } });
 * // => 42  (raw number, preserved for body/query serialization)
 *
 * @example
 * interpolate('Hello {{context.name}}!', { context: { name: 'Ada' } });
 * // => 'Hello Ada!'
 *
 * @example
 * interpolate('{{items[*].id}}', { items: [{ id: 'a' }, { id: 'b' }] });
 * // => ['a', 'b']  (raw array)
 *
 * @example
 * interpolate('ids={{items[*].id}}', { items: [{ id: 'a' }, { id: 'b' }] });
 * // => 'ids=a,b'   (array comma-joined in mixed template)
 */
export function interpolate(
  template: string,
  context: unknown,
  options?: InterpolateOptions,
): unknown {
  const { segments } = parse(template);
  const encodeSubstitutions = options?.encodeSubstitutions === true;
  const restoreLiterals = options?.onMissing === 'literal';

  if (segments.length === 0) {
    return '';
  }

  if (segments.length === 1) {
    const only = segments[0];
    if (only.type === 'expression') {
      const value = resolveExpression(only, context);
      if (value === undefined && restoreLiterals && only.kind === 'path') {
        return literalToken(only);
      }
      return value;
    }
    return only.value;
  }

  let out = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.type === 'literal') {
      out += seg.value;
      continue;
    }
    const value = resolveExpression(seg, context);
    if (value === undefined && restoreLiterals && seg.kind === 'path') {
      out += literalToken(seg);
    } else {
      out += coerceToString(value, encodeSubstitutions);
    }
  }
  return out;
}

function resolveExpression(expr: Expression, context: unknown): unknown {
  if (expr.kind === 'functionCall') {
    return undefined;
  }
  if (expr.kind === 'path') {
    const scoped = readPublishedScopePath(context, expr.segments);
    if (scoped !== SCOPE_PATH_PASSTHROUGH) {
      return scoped;
    }
  }
  return getByPath(context, expr.segments);
}

/** Reconstruct the original `{{<raw>}}` token for literal-fallback rendering. */
function literalToken(expr: Expression): string {
  return `{{${expr.raw}}}`;
}

function coerceToString(value: unknown, urlEncode = false): string {
  if (isUnpublishedScope(value)) {
    const label = `[unpublished scope: ${value.key}]`;
    return urlEncode ? encodeURIComponent(label) : label;
  }
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map((v) => coerceToString(v, urlEncode)).join(',');
  }
  if (typeof value === 'object') {
    try {
      const serialized = JSON.stringify(value) ?? '';
      return urlEncode ? encodeURIComponent(serialized) : serialized;
    } catch {
      return '';
    }
  }
  const asString = String(value);
  return urlEncode ? encodeURIComponent(asString) : asString;
}
